// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { fetchStudentWorkGallery, fetchWorkGalleryFile } from '../services/api';

const SOURCE_LABELS = {
  published: 'Published work',
  submission: 'Submissions',
  evidence: 'Assessment evidence',
};

const SOURCE_BADGES = {
  published: { label: 'Published', background: '#e0f2fe', color: '#075985' },
  submission: { label: 'Submission', background: '#dcfce7', color: '#166534' },
  evidence: { label: 'Evidence', background: '#fef3c7', color: '#92400e' },
};

const isImage = (mimeType) => /^image\//i.test(mimeType || '');

function itemContextLine(item) {
  const context = item.context || {};
  if (item.source === 'evidence') {
    return [context.standard_code, context.title].filter(Boolean).join(' · ') || 'Assessment evidence';
  }
  const status = context.evaluated ? 'Evaluated' : 'Not yet evaluated';
  return `${context.task_title || 'Learning task'} · ${status}`;
}

function GalleryItem({ item, busy, onOpen }) {
  const badge = SOURCE_BADGES[item.source] || SOURCE_BADGES.published;
  return (
    <li style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, display: 'grid', gap: 6, background: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'start' }}>
        <strong style={{ fontSize: 14, color: '#1e3a5f', overflowWrap: 'anywhere' }}>{item.name}</strong>
        <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap', background: badge.background, color: badge.color }}>
          {badge.label}
        </span>
      </div>
      <span style={{ fontSize: 12, color: '#475569' }}>{itemContextLine(item)}</span>
      <span style={{ fontSize: 12, color: '#64748b' }}>
        {item.date ? new Date(item.date).toLocaleDateString() : 'No date'}
        {item.author_role ? ` · Added by ${item.author_role}` : ''}
        {isImage(item.mime_type) ? ' · Picture' : ' · Document'}
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() => onOpen(item)}
        style={{ justifySelf: 'start', border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px', background: '#fff', color: '#1e3a5f', fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}
      >
        {busy ? 'Opening…' : 'Open'}
      </button>
    </li>
  );
}

/**
 * Browsable gallery of a student's school work: files teachers published on
 * learning tasks, submission uploads, and assessment evidence. The viewer's
 * role decides what the backend returns (families only see evaluated work and
 * shared evidence).
 */
export default function WorkGallery({ studentId, heading = 'Work gallery' }) {
  const [gallery, setGallery] = useState<any>(null);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [filter, setFilter] = useState<any>('all');
  const [openingId, setOpeningId] = useState<any>(null);

  useEffect(() => {
    if (studentId == null) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setGallery(null);
    fetchStudentWorkGallery(studentId)
      .then((data) => { if (!cancelled) setGallery(data); })
      .catch((loadError) => { if (!cancelled) setError(loadError.message || 'Could not load the work gallery.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  const items = gallery?.items || [];
  const sources = useMemo(() => {
    const present = new Set(items.map((item) => item.source));
    return Object.keys(SOURCE_LABELS).filter((source) => present.has(source));
  }, [items]);
  const visible = filter === 'all' ? items : items.filter((item) => item.source === filter);

  const openItem = async (item) => {
    setError('');
    setOpeningId(item.id);
    const popup = window.open('', '_blank');
    try {
      const blob = await fetchWorkGalleryFile(item.file);
      const typed = item.mime_type && blob.type !== item.mime_type ? new Blob([blob], { type: item.mime_type }) : blob;
      const url = URL.createObjectURL(typed);
      if (popup) popup.location.href = url;
      else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (openError: any) {
      if (popup) popup.close();
      setError(openError.message || 'Could not open this file.');
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <section aria-label={heading} style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: '#1e3a5f' }}>{heading}</h2>
          {gallery?.student && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
              {gallery.student.name}{gallery.student.grade_level ? ` · Grade ${gallery.student.grade_level}` : ''} · {items.length} item{items.length === 1 ? '' : 's'}
            </p>
          )}
        </div>
        {sources.length > 1 && (
          <div role="group" aria-label="Filter work by type" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['all', ...sources].map((source) => (
              <button
                key={source}
                type="button"
                onClick={() => setFilter(source)}
                aria-pressed={filter === source}
                style={{
                  border: '1px solid #cbd5e1', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  background: filter === source ? '#1e3a5f' : '#fff', color: filter === source ? '#fff' : '#1e3a5f',
                }}
              >
                {source === 'all' ? 'All work' : SOURCE_LABELS[source]}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div>}
      {loading ? (
        <p role="status" style={{ color: '#64748b', fontSize: 13 }}>Loading work gallery…</p>
      ) : !visible.length ? (
        <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.6 }}>
          No work to show yet. Files appear here when teachers publish learning task materials,
          students submit work, or assessment evidence is shared.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {visible.map((item) => (
            <GalleryItem key={item.id} item={item} busy={openingId === item.id} onOpen={openItem} />
          ))}
        </ul>
      )}
    </section>
  );
}
